/**
 * Компонент для редактирования свойств слоя сценария, сгруппированных по категориям
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { LAYER_ATTRIBUTE_CATEGORIES, LayerPropertiesViewMode, LIGHT_MODE_ATTRIBUTE_NAMES, getAttributeCategory } from '@/data/layerAttributeCategories';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { formatAttributeLabel } from '@/utils/attributeLabel';

const PROPERTY_DECIMAL_PLACES = 3;

interface LayerPropertyForm {
  attribute_id: string;
  attribute_name: string;
  attribute_unit?: string | null;
  attribute_type: string;
  string_value?: string;
  float_value?: number;
  integer_value?: number;
  _inputString?: string;
}

interface ScenarioLayerPropertiesEditByCategoryProps {
  properties: LayerPropertyForm[];
  tempProperties?: LayerPropertyForm[];
  onPropertyChange: (attributeId: string, field: 'string_value' | 'float_value' | 'integer_value', value: string | number | undefined) => void;
  onTempPropertyUpdate?: (properties: LayerPropertyForm[]) => void;
  onPropertyUpdate?: (properties: LayerPropertyForm[]) => void;
  loading?: boolean;
  /** light = subset of fields; pro = all grouped attributes. Does not affect saved data. */
  viewMode?: LayerPropertiesViewMode;
}

export const ScenarioLayerPropertiesEditByCategory: React.FC<ScenarioLayerPropertiesEditByCategoryProps> = ({
  properties,
  tempProperties = [],
  onPropertyChange,
  onTempPropertyUpdate,
  onPropertyUpdate,
  loading = false,
  viewMode = 'pro',
}) => {
  // Используем tempProperties если они есть, иначе обычные properties
  const displayProperties = tempProperties.length > 0 ? tempProperties : properties;

  // Создаем мапу свойств по имени атрибута для быстрого доступа
  const propertiesMap = new Map<string, LayerPropertyForm>();
  displayProperties.forEach(prop => {
    if (isHiddenLayerAttribute(prop.attribute_id, prop.attribute_name)) {
      return;
    }
    if (prop.attribute_name) {
      propertiesMap.set(prop.attribute_name, prop);
    }
  });

  // Группируем свойства по категориям
  const categorizedProperties = LAYER_ATTRIBUTE_CATEGORIES.map(category => {
    const categoryProps = category.attributes
      .map(attrName => {
        const prop = propertiesMap.get(attrName);
        if (!prop) return null;

        return {
          ...prop,
          category: category,
        };
      })
      .filter((prop): prop is LayerPropertyForm & { category: typeof category } => prop !== null);

    return {
      category,
      properties: categoryProps,
    };
  }).filter(group => group.properties.length > 0);

  const lightModeProperties = LIGHT_MODE_ATTRIBUTE_NAMES
    .map((attributeName) => propertiesMap.get(attributeName))
    .filter((prop): prop is LayerPropertyForm => prop != null);

  const fallbackCategory = LAYER_ATTRIBUTE_CATEGORIES[0];

  const handlePropertyChange = (attributeId: string, field: 'string_value' | 'float_value' | 'integer_value', value: string | number | undefined) => {
    onPropertyChange(attributeId, field, value);
  };

  const handleFloatChange = (property: LayerPropertyForm, inputValue: string) => {
    // Update the input string for display
    const props = tempProperties.length > 0 ? tempProperties : properties;
    const updatedProps = props.map(prop =>
      prop.attribute_id === property.attribute_id
        ? { ...prop, _inputString: inputValue }
        : prop
    );

    if (tempProperties.length > 0) {
      // Update temp properties
      if (onTempPropertyUpdate) {
        onTempPropertyUpdate(updatedProps);
      }
    } else {
      // Update main properties
      if (onPropertyUpdate) {
        onPropertyUpdate(updatedProps);
      }
    }

    // Allow empty string
    if (inputValue === '') {
      handlePropertyChange(property.attribute_id, 'float_value', undefined);
      return;
    }

    // Allow intermediate states: "-", "0", "0.", "0.3", etc.
    const floatRegex = /^-?\d*(\.\d{0,3})?$/;
    if (floatRegex.test(inputValue)) {
      // Try to parse - if valid number, save it
      const parsed = parseFloat(inputValue);
      if (!isNaN(parsed) && inputValue !== '-' && inputValue !== '.') {
        handlePropertyChange(property.attribute_id, 'float_value', parsed);
      }
    }
  };

  const handleFloatBlur = (property: LayerPropertyForm) => {
    const props = tempProperties.length > 0 ? tempProperties : properties;
    const currentProp = props.find(p => p.attribute_id === property.attribute_id);
    const inputValue = (currentProp as any)?._inputString ?? '';
    const trimmedValue = inputValue.trim();

    if (trimmedValue === '') {
      handlePropertyChange(property.attribute_id, 'float_value', undefined);
      const updatedProps = props.map(prop =>
        prop.attribute_id === property.attribute_id
          ? { ...prop, _inputString: undefined }
          : prop
      );
      if (tempProperties.length > 0) {
        if (onTempPropertyUpdate) {
          onTempPropertyUpdate(updatedProps);
        }
      } else {
        if (onPropertyUpdate) {
          onPropertyUpdate(updatedProps);
        }
      }
    } else {
      const parsed = parseFloat(trimmedValue);
      if (!isNaN(parsed)) {
        handlePropertyChange(property.attribute_id, 'float_value', parsed);
        const updatedProps = props.map(prop =>
          prop.attribute_id === property.attribute_id
            ? { ...prop, _inputString: undefined }
            : prop
        );
        if (tempProperties.length > 0) {
          if (onTempPropertyUpdate) {
            onTempPropertyUpdate(updatedProps);
          }
        } else {
          if (onPropertyUpdate) {
            onPropertyUpdate(updatedProps);
          }
        }
      }
    }
  };

  const handleIntegerChange = (property: LayerPropertyForm, inputValue: string) => {
    const props = tempProperties.length > 0 ? tempProperties : properties;
    const updatedProps = props.map(prop =>
      prop.attribute_id === property.attribute_id
        ? { ...prop, _inputString: inputValue }
        : prop
    );

    if (tempProperties.length > 0) {
      // Update temp properties
      if (onTempPropertyUpdate) {
        onTempPropertyUpdate(updatedProps);
      }
    } else {
      // Update main properties
      if (onPropertyUpdate) {
        onPropertyUpdate(updatedProps);
      }
    }

    if (inputValue === '') {
      handlePropertyChange(property.attribute_id, 'integer_value', undefined);
      return;
    }

    const intRegex = /^-?\d*$/;
    if (intRegex.test(inputValue)) {
      const parsed = parseInt(inputValue, 10);
      if (!isNaN(parsed) && inputValue !== '-') {
        handlePropertyChange(property.attribute_id, 'integer_value', parsed);
      }
    }
  };

  const handleIntegerBlur = (property: LayerPropertyForm) => {
    const props = tempProperties.length > 0 ? tempProperties : properties;
    const currentProp = props.find(p => p.attribute_id === property.attribute_id);
    const inputValue = (currentProp as any)?._inputString ?? '';
    const trimmedValue = inputValue.trim();

    if (trimmedValue === '') {
      handlePropertyChange(property.attribute_id, 'integer_value', undefined);
      const updatedProps = props.map(prop =>
        prop.attribute_id === property.attribute_id
          ? { ...prop, _inputString: undefined }
          : prop
      );
      if (tempProperties.length > 0) {
        if (onTempPropertyUpdate) {
          onTempPropertyUpdate(updatedProps);
        }
      } else {
        if (onPropertyUpdate) {
          onPropertyUpdate(updatedProps);
        }
      }
    } else {
      const parsed = parseInt(trimmedValue, 10);
      if (!isNaN(parsed)) {
        handlePropertyChange(property.attribute_id, 'integer_value', parsed);
        const updatedProps = props.map(prop =>
          prop.attribute_id === property.attribute_id
            ? { ...prop, _inputString: undefined }
            : prop
        );
        if (tempProperties.length > 0) {
          if (onTempPropertyUpdate) {
            onTempPropertyUpdate(updatedProps);
          }
        } else {
          if (onPropertyUpdate) {
            onPropertyUpdate(updatedProps);
          }
        }
      }
    }
  };

  const renderPropertyInput = (property: LayerPropertyForm & { category: typeof LAYER_ATTRIBUTE_CATEGORIES[0] }) => {
    const { attribute_id, attribute_name, attribute_type } = property;
    const props = tempProperties.length > 0 ? tempProperties : properties;
    const currentProp = props.find(p => p.attribute_id === attribute_id);

    if (!currentProp) {
      return null;
    }

    switch (attribute_type) {
      case 'string':
        return (
          <TextField
            key={attribute_id}
            label={formatAttributeLabel(attribute_name, property.attribute_unit)}
            value={currentProp.string_value || ''}
            onChange={(e) => handlePropertyChange(attribute_id, 'string_value', e.target.value)}
            fullWidth
            size="small"
          />
        );
      case 'float':
        const floatDisplayValue = (currentProp as any)?._inputString ??
          (currentProp.float_value !== undefined && currentProp.float_value !== null
            ? Number(currentProp.float_value).toFixed(PROPERTY_DECIMAL_PLACES)
            : '');

        return (
          <TextField
            key={attribute_id}
            label={formatAttributeLabel(attribute_name, property.attribute_unit)}
            type="text"
            value={floatDisplayValue}
            onChange={(e) => handleFloatChange(property, e.target.value)}
            onBlur={() => handleFloatBlur(property)}
            fullWidth
            size="small"
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*', step: '0.001' }}
          />
        );
      case 'integer':
        const intDisplayValue = (currentProp as any)?._inputString ??
          (currentProp.integer_value !== undefined && currentProp.integer_value !== null ? String(currentProp.integer_value) : '');

        return (
          <TextField
            key={attribute_id}
            label={formatAttributeLabel(attribute_name, property.attribute_unit)}
            type="text"
            value={intDisplayValue}
            onChange={(e) => handleIntegerChange(property, e.target.value)}
            onBlur={() => handleIntegerBlur(property)}
            fullWidth
            size="small"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (viewMode === 'light') {
    if (lightModeProperties.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No light-mode properties available
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {lightModeProperties.map((property) => (
          <Grid item xs={12} sm={6} md={4} key={property.attribute_id}>
            {renderPropertyInput({ ...property, category: getAttributeCategory(property.attribute_name) ?? fallbackCategory })}
          </Grid>
        ))}
      </Grid>
    );
  }

  if (categorizedProperties.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No properties available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {categorizedProperties.map(({ category, properties: categoryProps }) => (
        <Accordion key={category.id} defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${category.id}-content`}
            id={`${category.id}-header`}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              {category.name}
            </Typography>
            <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
              ({categoryProps.length} {categoryProps.length === 1 ? 'attribute' : 'attributes'})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {categoryProps.map((property) => (
                <Grid item xs={12} sm={6} md={4} key={property.attribute_id}>
                  {renderPropertyInput(property)}
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
